import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Clock,
  Activity,
  Download,
  FileSpreadsheet,
  Flame,
  Palmtree,
  Percent,
  PlusSquare,
  RefreshCw,
  Layers,
  Users,
  Wallet,
  Landmark,
  Pencil,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchPeriod, type PeriodRow } from "@/lib/periods-api";
import { getAccessToken } from "@/lib/local-auth";
import {
  payrollPeriodKeyFromEndDate,
  formatClockLikeTo24h,
  formatDateDmyFromDate,
  formatDateDmyFromYmd,
  formatIDR,
  parseToYmd,
} from "@/lib/format";
import {
  fetchRatesByPeriod,
  saveRateIncentive,
  saveRateTax,
  saveRatePaidLeave,
  type RateIncentiveItem,
  type RateTaxItem,
  type RatePaidLeavePayload,
  type RateRow,
} from "@/lib/rate-api";
import {
  hmsToSeconds,
  secondsToHms,
  hhDotMmToSeconds,
  safeAdditionalHoursPayload,
  safeDetailsProject,
  safeReimbursePayload,
  parseRateIncentiveItems,
  incentiveTotalFromRate,
  parseRateTaxItems,
  taxTotalFromRate,
  safePaidLeavePayload,
  summarizeRates,
  computeCutiAdjustedTotalsForRateRow,
  cutiHangusEffectForAdditionalRow,
  cutiHangusEffectForSession,
  type DetailsProject,
} from "@/lib/rate-row-helpers";
import { Calendar, CalendarDayButton } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  calculatePayslip,
  mergePayrollSnapshotFromRates,
  setActivePayrollPeriod,
  uid,
  upsertPayrollPeriod,
  useHR,
} from "@/lib/hr-store";
import { downloadPayslipPDF } from "@/lib/payslip-pdf";
import { downloadAllEarnStyleXlsx } from "@/lib/all-earn-xlsx";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/periode/$periodId")({
  component: PeriodeDetailLayout,
});

type SessionDetail = {
  rowNumber?: number;
  task?: string | null;
  workSessionType?: string | null;
  dateStart?: string | null;
  dateEnd?: string | null;
  timeStart?: string | null;
  timeEnd?: string | null;
  loggedTime?: string | null;
  activeTime?: string | null;
  loggedSeconds?: number | null;
  activeSeconds?: number | null;
  activityPct?: number | null;
  cost?: number | null;
};

function formatHourlyRateIdr(raw: number | null): string {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return "-";
  // API timesheet kadang mengirim rate dalam ribuan (mis 32.14 => Rp 32.140)
  return formatIDR(raw * 1000);
}

function formatCostIdr(raw: number | null | undefined): string {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return "-";
  // API timesheet kadang mengirim cost dalam ribuan (mis 3485.03 => Rp 3.485.030)
  return formatIDR(raw * 1000);
}

/** Hilangkan semua non-digit lalu kembalikan integer positif (atau 0). */
function parseRupiahDigits(raw: unknown): number {
  const digits = String(raw ?? "").replace(/[^0-9]/g, "");
  if (!digits) return 0;
  const n = Number(digits);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Format angka jadi pemisah ribuan id-ID tanpa simbol mata uang (mis. 1.234.567). */
function formatRupiahDigits(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  return new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(Math.floor(n));
}

function safeSessions(p: any): SessionDetail[] {
  if (!p || typeof p !== "object") return [];
  return Array.isArray(p.sessions) ? (p.sessions as SessionDetail[]) : [];
}

/** En-dash rentang jam → tanda hubung ASCII agar mudah dipindai (mis. 07:33-07:59). */
function formatCountedTimeRangesForDisplay(label: string): string {
  return label.replace(/–/g, "-");
}

/**
 * Tanggal + status cuti/hangus: tanggal kiri, badge kanan (rapi di mobile & desktop).
 */
function CutiHangusDateCell({
  dateLine,
  fullHangus,
  partialHangus,
  partialCountedLabel,
}: {
  dateLine: ReactNode;
  fullHangus: boolean;
  partialHangus: boolean;
  partialCountedLabel?: string | null;
}) {
  const partialLabel = partialCountedLabel ? formatCountedTimeRangesForDisplay(partialCountedLabel) : null;
  const showBadges = fullHangus || (partialHangus && partialLabel);

  return (
    <div className="flex min-w-0 flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-x-4">
      <span className="shrink-0 tabular-nums leading-snug text-foreground">{dateLine}</span>
      {showBadges ? (
        <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:max-w-[min(100%,20rem)] sm:items-end">
          {fullHangus ? (
            <span
              className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-lg border border-destructive/25 bg-destructive/10 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-destructive shadow-sm dark:border-destructive/40 dark:bg-destructive/20"
              title="Seluruh waktu dalam rentang lokal 08:00–16:01 (hari cuti tanpa wajib kerja) — tidak dihitung."
            >
              <Flame className="size-3.5 shrink-0 opacity-90" strokeWidth={2.2} aria-hidden />
              <span className="sr-only">Tidak dihitung: seluruh rentang dalam jam cuti.</span>
              <span className="leading-tight">Hangus</span>
            </span>
          ) : null}
          {partialHangus && partialLabel ? (
            <span
              className="inline-flex w-fit max-w-full flex-col gap-1 rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-2 shadow-sm dark:border-orange-800/55 dark:bg-orange-950/45"
              title="Bagian dalam rentang lokal 08:00–16:01 tidak dihitung (hari cuti tanpa wajib kerja)."
            >
              <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase leading-tight tracking-wide text-orange-600 dark:text-orange-400">
                <Flame className="size-3.5 shrink-0 text-orange-500 dark:text-orange-400" strokeWidth={2.2} aria-hidden />
                Sebagian hangus
              </span>
              <span className="text-[11px] font-medium leading-snug tracking-normal text-orange-600/95 tabular-nums dark:text-orange-300">
                Dihitung: {partialLabel}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CutiCalendarDayButton(props: ComponentProps<typeof CalendarDayButton>) {
  const { day, modifiers, className, ...rest } = props;
  const mod = modifiers as { cutiMandatedWork?: boolean };
  const isConflict = Boolean(mod.cutiMandatedWork);
  return (
    <span className="relative inline-flex">
      <CalendarDayButton
        day={day}
        modifiers={modifiers}
        className={cn(className, isConflict && "ring-2 ring-orange-400/75 ring-offset-1 ring-offset-background")}
        {...rest}
      />
      {isConflict ? (
        <span
          className="pointer-events-none absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-sm bg-orange-500/95 shadow-sm"
          title="Hari cuti dengan wajib kerja (True)"
          aria-hidden
        >
          <Flame className="size-2.5 text-white" strokeWidth={2.2} />
        </span>
      ) : null}
    </span>
  );
}

/** Konversi Date → YYYY-MM-DD local. */
function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYmdLocal(s: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function SessionsByProject({
  projects,
  report,
  paidLeave,
}: {
  projects: any[];
  report?: DetailsProject["report"];
  paidLeave: RatePaidLeavePayload | null;
}) {
  /** Beberapa project boleh terbuka sekaligus — menghindari collapse tinggi saat ganti project (UX scroll). */
  const [openProjectKeys, setOpenProjectKeys] = useState<string[]>([]);
  const projectsWithSessions = (projects ?? []).filter((p: any) => safeSessions(p).length > 0);
  if (projectsWithSessions.length === 0) return null;

  const dr = report?.dateRange;
  let reportRangeLabel: string | null = null;
  if (dr?.from && dr?.to) {
    const a = new Date(dr.from);
    const b = new Date(dr.to);
    if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime())) {
      reportRangeLabel = `${formatDateDmyFromDate(a)} → ${formatDateDmyFromDate(b)}`;
    }
  }
  if (!reportRangeLabel && dr?.raw) reportRangeLabel = dr.raw;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Detail sesi (per project)
        </p>
        {reportRangeLabel || report?.timezone ? (
          <p className="text-[11px] text-muted-foreground">
            {reportRangeLabel ? <span>{reportRangeLabel}</span> : null}
            {reportRangeLabel && report?.timezone ? <span className="px-1">·</span> : null}
            {report?.timezone ? <span>{report.timezone}</span> : null}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        {projectsWithSessions.map((p: any, idx: number) => {
          const sessions = safeSessions(p);
          const key = `${p.project}-${idx}`;
          const isOpen = openProjectKeys.includes(key);
          return (
            <div
              key={key}
              className={cn(
                "overflow-hidden rounded-xl border border-border/60 bg-background transition-[box-shadow,border-color] duration-200",
                isOpen && "border-primary/35 shadow-sm",
              )}
            >
              <button
                type="button"
                onClick={() =>
                  setOpenProjectKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]))
                }
                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
                aria-expanded={isOpen}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 transition-transform duration-200 ease-out motion-reduce:duration-0",
                        isOpen && "-rotate-180",
                      )}
                      aria-hidden
                    />
                  </span>
                  <span className="truncate text-sm font-medium">{String(p.project ?? "-")}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {sessions.length} sesi
                  </span>
                </div>
                <div className="hidden text-[11px] text-muted-foreground sm:flex sm:items-center sm:gap-3">
                  <span className="tabular-nums">{String(p.loggedTime ?? "-")}</span>
                  <span>· {String(p.activityPct ?? 0)}%</span>
                  <span>· {formatCostIdr(p.cost)}</span>
                </div>
              </button>
              <div
                className={cn(
                  "grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:duration-0",
                  isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                )}
              >
                <div className="min-h-0">
                  <div
                    className={cn(
                      "overflow-x-auto border-t border-border/60 bg-muted/10",
                      !isOpen && "pointer-events-none",
                    )}
                    aria-hidden={!isOpen}
                  >
                    <table className="w-full min-w-[640px] text-xs sm:text-sm">
                      <thead className="bg-muted/50 [&_th]:font-medium [&_th]:text-muted-foreground">
                        <tr className="text-left">
                          <th className="px-3 py-2">Tanggal</th>
                          <th className="px-3 py-2">Jam mulai</th>
                          <th className="px-3 py-2">Jam selesai</th>
                          <th className="px-3 py-2 text-right">Durasi</th>
                          <th className="px-3 py-2 text-right">Aktif</th>
                          <th className="px-3 py-2 text-right">Aktivitas</th>
                          <th className="px-3 py-2 text-right">Biaya</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((s, sIdx) => {
                          const hang = cutiHangusEffectForSession(paidLeave, s);
                          const fullHangus = hang.applicable && hang.voidRatio >= 1;
                          const partialHangus = hang.applicable && hang.voidRatio > 0 && hang.voidRatio < 1;
                          return (
                            <tr
                              key={`${s.rowNumber ?? sIdx}`}
                              className={cn(
                                "border-t",
                                fullHangus && "bg-destructive/10 text-destructive [&_td]:text-destructive",
                                partialHangus &&
                                  "bg-orange-50/90 text-foreground dark:bg-orange-950/30 [&_td]:text-foreground",
                              )}
                            >
                              <td className="px-3 py-2.5 align-top tabular-nums">
                                <CutiHangusDateCell
                                  dateLine={
                                    <>
                                      {formatDateDmyFromYmd(s.dateStart)}
                                      {s.dateEnd && s.dateEnd !== s.dateStart
                                        ? ` → ${formatDateDmyFromYmd(s.dateEnd)}`
                                        : ""}
                                    </>
                                  }
                                  fullHangus={fullHangus}
                                  partialHangus={partialHangus}
                                  partialCountedLabel={hang.meta?.countedSegmentsLabel ?? null}
                                />
                              </td>
                              <td className="px-3 py-2 tabular-nums">{formatClockLikeTo24h(s.timeStart)}</td>
                              <td className="px-3 py-2 tabular-nums">{formatClockLikeTo24h(s.timeEnd)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{s.loggedTime ?? "-"}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{s.activeTime ?? "-"}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {typeof s.activityPct === "number" ? `${s.activityPct}%` : "-"}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {typeof s.cost === "number" ? formatCostIdr(s.cost) : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function employeeInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, Math.min(2, parts[0].length)).toUpperCase();
  const a = parts[0][0] ?? "";
  const b = parts[parts.length - 1][0] ?? "";
  return `${a}${b}`.toUpperCase();
}

/** Aksen gradient kartu karyawan — variasi hijau sage / hutan (selaras tema) */
const CARD_ACCENT_BAR = [
  "from-primary to-secondary",
  "from-primary via-secondary to-muted",
  "from-secondary to-primary",
  "from-muted via-secondary to-primary",
  "from-primary to-muted",
  "from-secondary via-primary to-muted",
] as const;

type InsentifJenis = "jabatan" | "wfo";

const INSENTIF_TYPES: { key: InsentifJenis; label: string }[] = [
  { key: "jabatan", label: "Insentif Jabatan" },
  { key: "wfo", label: "Insentif WFO" },
];

type InsentifFormDraft = {
  id: string;
  type: InsentifJenis;
  amount: number;
  note: string;
};

const emptyInsForm: InsentifFormDraft = { id: "", type: "jabatan", amount: 0, note: "" };

type PajakJenis = "pph21" | "pph23" | "lainnya";

const PAJAK_TYPES: { key: PajakJenis; label: string }[] = [
  { key: "pph21", label: "PPh 21" },
  { key: "pph23", label: "PPh 23" },
  { key: "lainnya", label: "Pajak lainnya" },
];

type PajakFormDraft = {
  id: string;
  type: PajakJenis;
  amount: number;
  note: string;
};

const emptyTaxForm: PajakFormDraft = { id: "", type: "pph21", amount: 0, note: "" };

const EMPLOYEE_DETAIL_MOTION_MS = 300;
/** Tunggu layout setelah grid 0fr→1fr sebelum mengukur posisi judul detail. */
const DETAIL_SCROLL_AFTER_EXPAND_MS = EMPLOYEE_DETAIL_MOTION_MS + 80;

function getVerticalScrollParent(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null;
  while (el && el !== document.body) {
    const { overflowY } = window.getComputedStyle(el);
    const oy = overflowY;
    const scrollable =
      (oy === "auto" || oy === "scroll" || oy === "overlay") && el.scrollHeight > el.clientHeight + 1;
    if (scrollable) return el;
    el = el.parentElement;
  }
  return null;
}

/** Gulir agar elemen sejajar ke atas area lihat, dengan offset + scroll-margin pada elemen. */
function scrollPageToElementTop(el: HTMLElement, behavior: ScrollBehavior, extraOffsetPx: number) {
  const marginTop = parseFloat(window.getComputedStyle(el).scrollMarginTop);
  const offset = extraOffsetPx + (Number.isFinite(marginTop) ? marginTop : 0);

  const scrollParent = getVerticalScrollParent(el);
  if (!scrollParent) {
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior });
    return;
  }
  const rootRect = scrollParent.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const nextTop = scrollParent.scrollTop + (elRect.top - rootRect.top) - offset;
  scrollParent.scrollTo({ top: Math.max(0, nextTop), behavior });
}

function PeriodeDetailLayout() {
  const { periodId } = Route.useParams();
  const employees = useHR((s) => s.employees);
  const [period, setPeriod] = useState<PeriodRow | null>(null);
  const [expandedEmployee, setExpandedEmployee] = useState<string | null>(null);
  /** Saat true, panel baris yang sedang expanded di-collapse (animasi tutup) sebelum unmount. */
  const [detailCollapsed, setDetailCollapsed] = useState(false);
  const detailCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushDetailCollapseTimer = useCallback(() => {
    if (detailCollapseTimerRef.current) {
      clearTimeout(detailCollapseTimerRef.current);
      detailCollapseTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => flushDetailCollapseTimer();
  }, [flushDetailCollapseTimer]);

  const toggleEmployeeDetail = useCallback(
    (name: string) => {
      flushDetailCollapseTimer();
      const reduceMotion =
        typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      const isThisRow = expandedEmployee === name;
      const isFullyOpen = isThisRow && !detailCollapsed;
      if (isThisRow && detailCollapsed) {
        setExpandedEmployee(null);
        setDetailCollapsed(false);
        return;
      }
      if (isFullyOpen) {
        if (reduceMotion) {
          setExpandedEmployee(null);
          setDetailCollapsed(false);
          return;
        }
        setDetailCollapsed(true);
        detailCollapseTimerRef.current = setTimeout(() => {
          setExpandedEmployee(null);
          setDetailCollapsed(false);
          detailCollapseTimerRef.current = null;
        }, EMPLOYEE_DETAIL_MOTION_MS);
        return;
      }
      setExpandedEmployee(name);
      if (reduceMotion) {
        setDetailCollapsed(false);
        return;
      }
      setDetailCollapsed(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDetailCollapsed(false);
        });
      });
    },
    [detailCollapsed, expandedEmployee, flushDetailCollapseTimer],
  );

  const [rates, setRates] = useState<RateRow[]>([]);
  const ratesRef = useRef(rates);
  ratesRef.current = rates;

  useEffect(() => {
    if (!expandedEmployee || detailCollapsed) return;

    const reduceMotion =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    let cancelled = false;
    let attempts = 0;

    const run = () => {
      if (cancelled) return;
      const row = ratesRef.current.find((x) => x.name_employee === expandedEmployee);
      if (!row) return;
      const el = document.getElementById(`rate-detail-heading-${row.id}`);
      if (!el) {
        attempts += 1;
        if (attempts < 24) requestAnimationFrame(run);
        return;
      }
      scrollPageToElementTop(el, reduceMotion ? "auto" : "smooth", 12);
    };

    if (reduceMotion) {
      queueMicrotask(() => {
        requestAnimationFrame(run);
      });
      return () => {
        cancelled = true;
      };
    }

    const t = window.setTimeout(run, DETAIL_SCROLL_AFTER_EXPAND_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [detailCollapsed, expandedEmployee]);

  const [ratesLoading, setRatesLoading] = useState(false);
  const [cutiOpen, setCutiOpen] = useState(false);
  const [cutiName, setCutiName] = useState("");
  const [cutiDates, setCutiDates] = useState<Date[]>([]);
  const [cutiReason, setCutiReason] = useState("");
  const [cutiProof, setCutiProof] = useState<"terlampir" | "tidak terlampir">("tidak terlampir");
  const [cutiHoursPerDay, setCutiHoursPerDay] = useState("8");
  const [cutiSaving, setCutiSaving] = useState(false);
  const [cutiDeleteOpen, setCutiDeleteOpen] = useState(false);
  /** YYYY-MM-DD: hari cuti di mana karyawan wajib kerja (disimpan sebagai `work_required_dates`). */
  const [cutiWorkRequiredYmds, setCutiWorkRequiredYmds] = useState<string[]>([]);
  const [insOpen, setInsOpen] = useState(false);
  const [insTargetName, setInsTargetName] = useState<string | null>(null);
  const [insDraft, setInsDraft] = useState<InsentifFormDraft>(emptyInsForm);
  const [insSaving, setInsSaving] = useState(false);
  const [insDeleteTarget, setInsDeleteTarget] = useState<{ name: string; id: string } | null>(null);
  const [insDeleteLoading, setInsDeleteLoading] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [taxTargetName, setTaxTargetName] = useState<string | null>(null);
  const [taxDraft, setTaxDraft] = useState<PajakFormDraft>(emptyTaxForm);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxDeleteTarget, setTaxDeleteTarget] = useState<{ name: string; id: string } | null>(null);
  const [taxDeleteLoading, setTaxDeleteLoading] = useState(false);
  const title = period
    ? (() => {
        const d = new Date(`${period.end_date}T00:00:00`);
        if (Number.isNaN(d.getTime())) return "Periode Penggajian";
        const label = d.toLocaleString("id-ID", { month: "long", year: "numeric" });
        return label.length ? label[0].toUpperCase() + label.slice(1) : "Periode Penggajian";
      })()
    : "Periode Penggajian";
  const range = period
    ? (() => {
        const start = new Date(`${period.start_date}T00:00:00`);
        const end = new Date(`${period.end_date}T00:00:00`);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return `${formatDateDmyFromYmd(period.start_date)} → ${formatDateDmyFromYmd(period.end_date)}`;
        }
        return `${format(start, "dd-MM-yyyy")} → ${format(end, "dd-MM-yyyy")}`;
      })()
    : null;

  const payrollYm = useMemo(() => (period ? payrollPeriodKeyFromEndDate(period.end_date) : null), [period]);

  const periodStartDate = useMemo(() => {
    if (!period?.start_date) return null;
    const d = new Date(`${period.start_date}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [period?.start_date]);

  const periodEndDate = useMemo(() => {
    if (!period?.end_date) return null;
    const d = new Date(`${period.end_date}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [period?.end_date]);

  const isDateWithinPeriod = useCallback(
    (d: Date) => {
      if (!periodStartDate || !periodEndDate) return true;
      const t = d.getTime();
      return t >= periodStartDate.getTime() && t <= periodEndDate.getTime();
    },
    [periodStartDate, periodEndDate],
  );

  const resolveEmployeeId = useCallback((nameEmployee: string) => {
    const k = nameEmployee.trim().toLowerCase();
    return employees.find((e) => e.name.trim().toLowerCase() === k)?.id ?? null;
  }, [employees]);

  useEffect(() => {
    if (!payrollYm) return;
    upsertPayrollPeriod(payrollYm);
    setActivePayrollPeriod(payrollYm);
  }, [payrollYm]);

  useEffect(() => {
    if (!payrollYm || rates.length === 0) return;
    mergePayrollSnapshotFromRates(payrollYm, rates);
  }, [payrollYm, rates]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const p = await fetchPeriod(periodId);
        if (!alive) return;
        setPeriod(p);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Gagal memuat detail periode");
      }
    })();
    return () => {
      alive = false;
    };
  }, [periodId]);

  const loadRates = useCallback(async () => {
    if (!getAccessToken()) return;
    setRatesLoading(true);
    try {
      const data = await fetchRatesByPeriod(periodId);
      setRates(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal memuat data rate dari database");
    } finally {
      setRatesLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    void loadRates();
  }, [loadRates]);

  const ratesSummary = useMemo(() => summarizeRates(rates), [rates]);

  const openInsentifForEmployee = (nameEmployee: string) => {
    if (!resolveEmployeeId(nameEmployee)) {
      toast.error("Nama tidak cocok dengan data karyawan. Sesuaikan nama di menu Karyawan.");
      return;
    }
    if (!payrollYm) return;
    setActivePayrollPeriod(payrollYm);
    setInsTargetName(nameEmployee.trim());
    setInsDraft(emptyInsForm);
    setInsOpen(true);
  };

  const openEditInsentif = (nameEmployee: string, item: RateIncentiveItem) => {
    if (!resolveEmployeeId(nameEmployee)) {
      toast.error("Nama tidak cocok dengan data karyawan.");
      return;
    }
    if (!payrollYm) return;
    setActivePayrollPeriod(payrollYm);
    setInsTargetName(nameEmployee.trim());
    setInsDraft({
      id: item.id,
      type: item.jenis,
      amount: item.nominal,
      note: item.catatan ?? "",
    });
    setInsOpen(true);
  };

  const editingIns = !!insDraft.id.trim();

  const submitInsentif = async () => {
    if (!insTargetName || !(insDraft.amount > 0)) {
      toast.error("Isi nominal insentif (lebih dari 0).");
      return;
    }
    if (!getAccessToken()) {
      toast.error("Belum login.");
      return;
    }
    const row = rates.find((rr) => rr.name_employee.trim() === insTargetName.trim());
    const current = parseRateIncentiveItems(row?.incentive);
    const newId = insDraft.id.trim() ? insDraft.id.trim() : uid();
    const item: RateIncentiveItem = {
      id: newId,
      jenis: insDraft.type,
      nominal: insDraft.amount,
      catatan: insDraft.note.trim() || undefined,
    };
    const isEdit = current.some((x) => x.id === insDraft.id.trim());
    const next = isEdit ? current.map((x) => (x.id === item.id ? item : x)) : [...current, item];

    setInsSaving(true);
    try {
      await saveRateIncentive(periodId, insTargetName, next);
      await loadRates();
      toast.success(editingIns ? "Insentif diperbarui" : "Insentif disimpan ke database");
      setInsOpen(false);
      setInsDraft(emptyInsForm);
      setInsTargetName(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menyimpan insentif");
    } finally {
      setInsSaving(false);
    }
  };

  const confirmDeleteInsentif = async () => {
    if (!insDeleteTarget) return;
    const { name, id } = insDeleteTarget;
    if (!getAccessToken()) {
      toast.error("Belum login.");
      return;
    }
    const row = rates.find((rr) => rr.name_employee.trim() === name.trim());
    const current = parseRateIncentiveItems(row?.incentive);
    const next = current.filter((x) => x.id !== id);
    setInsDeleteLoading(true);
    try {
      await saveRateIncentive(periodId, name, next);
      await loadRates();
      toast.success("Insentif dihapus");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menghapus insentif");
    } finally {
      setInsDeleteLoading(false);
      setInsDeleteTarget(null);
    }
  };

  const openPajakForEmployee = (nameEmployee: string) => {
    if (!resolveEmployeeId(nameEmployee)) {
      toast.error("Nama tidak cocok dengan data karyawan. Sesuaikan nama di menu Karyawan.");
      return;
    }
    if (!payrollYm) return;
    setActivePayrollPeriod(payrollYm);
    setTaxTargetName(nameEmployee.trim());
    setTaxDraft(emptyTaxForm);
    setTaxOpen(true);
  };

  const openEditPajak = (nameEmployee: string, item: RateTaxItem) => {
    if (!resolveEmployeeId(nameEmployee)) {
      toast.error("Nama tidak cocok dengan data karyawan.");
      return;
    }
    if (!payrollYm) return;
    setActivePayrollPeriod(payrollYm);
    setTaxTargetName(nameEmployee.trim());
    setTaxDraft({
      id: item.id,
      type: item.jenis,
      amount: item.nominal,
      note: item.catatan ?? "",
    });
    setTaxOpen(true);
  };

  const editingTax = !!taxDraft.id.trim();

  const submitPajak = async () => {
    if (!taxTargetName || !(taxDraft.amount > 0)) {
      toast.error("Isi nominal pajak (lebih dari 0).");
      return;
    }
    if (!getAccessToken()) {
      toast.error("Belum login.");
      return;
    }
    const row = rates.find((rr) => rr.name_employee.trim() === taxTargetName.trim());
    const current = parseRateTaxItems(row?.tax);
    const newId = taxDraft.id.trim() ? taxDraft.id.trim() : uid();
    const item: RateTaxItem = {
      id: newId,
      jenis: taxDraft.type,
      nominal: taxDraft.amount,
      catatan: taxDraft.note.trim() || undefined,
    };
    const isEdit = current.some((x) => x.id === taxDraft.id.trim());
    const next = isEdit ? current.map((x) => (x.id === item.id ? item : x)) : [...current, item];

    setTaxSaving(true);
    try {
      await saveRateTax(periodId, taxTargetName, next);
      await loadRates();
      toast.success(editingTax ? "Pajak diperbarui" : "Pajak disimpan ke database");
      setTaxOpen(false);
      setTaxDraft(emptyTaxForm);
      setTaxTargetName(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menyimpan pajak");
    } finally {
      setTaxSaving(false);
    }
  };

  const confirmDeletePajak = async () => {
    if (!taxDeleteTarget) return;
    const { name, id } = taxDeleteTarget;
    if (!getAccessToken()) {
      toast.error("Belum login.");
      return;
    }
    const row = rates.find((rr) => rr.name_employee.trim() === name.trim());
    const current = parseRateTaxItems(row?.tax);
    const next = current.filter((x) => x.id !== id);
    setTaxDeleteLoading(true);
    try {
      await saveRateTax(periodId, name, next);
      await loadRates();
      toast.success("Pajak dihapus");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menghapus pajak");
    } finally {
      setTaxDeleteLoading(false);
      setTaxDeleteTarget(null);
    }
  };

  const openCutiForEmployee = (nameEmployee: string) => {
    if (!resolveEmployeeId(nameEmployee)) {
      toast.error("Nama tidak cocok dengan data karyawan. Sesuaikan nama di menu Karyawan.");
      return;
    }
    if (!payrollYm) return;
    setActivePayrollPeriod(payrollYm);
    const row = rates.find((rr) => rr.name_employee.trim() === nameEmployee.trim());
    const existing = safePaidLeavePayload(row?.paid_leave);
    setCutiName(nameEmployee.trim());
    setCutiDates(
      existing
        ? existing.dates
            .map((s) => parseYmdLocal(s))
            .filter((d): d is Date => !!d && isDateWithinPeriod(d))
        : [],
    );
    setCutiReason(existing?.reason ?? "");
    setCutiProof(existing?.proof ?? "tidak terlampir");
    setCutiHoursPerDay(String(existing?.hours_per_day ?? 8));
    const wr = (existing?.work_required_dates ?? []).filter((y) => (existing?.dates ?? []).includes(y));
    setCutiWorkRequiredYmds(wr);
    setCutiOpen(true);
  };

  const cutiTotalDays = cutiDates.length;
  const cutiHpd = Number(cutiHoursPerDay);
  const cutiTotalHours = Number.isFinite(cutiHpd) && cutiHpd > 0 ? cutiTotalDays * cutiHpd : cutiTotalDays * 8;
  const cutiEditing = (() => {
    const row = rates.find((rr) => rr.name_employee.trim() === cutiName.trim());
    return !!safePaidLeavePayload(row?.paid_leave);
  })();

  const cutiWorkRequiredSet = useMemo(() => new Set(cutiWorkRequiredYmds), [cutiWorkRequiredYmds]);

  const cutiCalendarModifiers = useMemo(
    () => ({
      cutiMandatedWork: cutiDates.filter((d) => cutiWorkRequiredSet.has(ymdLocal(d))),
    }),
    [cutiDates, cutiWorkRequiredSet],
  );

  const submitCuti = async () => {
    if (!cutiName.trim()) {
      toast.error("Karyawan belum dipilih.");
      return;
    }
    if (cutiDates.length === 0) {
      toast.error("Pilih minimal 1 tanggal cuti pada kalender.");
      return;
    }
    const outside = cutiDates.filter((d) => !isDateWithinPeriod(d));
    if (outside.length > 0) {
      toast.error(
        `Ada ${outside.length} tanggal di luar periode (${range ?? ""}). Hapus tanggal tersebut terlebih dahulu.`,
      );
      return;
    }
    if (!cutiReason.trim()) {
      toast.error("Alasan cuti wajib diisi.");
      return;
    }
    if (!getAccessToken()) {
      toast.error("Belum login.");
      return;
    }
    setCutiSaving(true);
    try {
      const sortedYmd = cutiDates.map((d) => ymdLocal(d)).sort();
      await saveRatePaidLeave(periodId, cutiName, {
        dates: sortedYmd,
        reason: cutiReason.trim(),
        proof: cutiProof,
        hoursPerDay: Number.isFinite(cutiHpd) && cutiHpd > 0 ? cutiHpd : 8,
        workRequiredDates: cutiWorkRequiredYmds.filter((ymd) => sortedYmd.includes(ymd)).sort(),
      });
      await loadRates();
      toast.success("Cuti berbayar tersimpan");
      setCutiOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menyimpan cuti");
    } finally {
      setCutiSaving(false);
    }
  };

  const confirmDeleteCuti = async () => {
    if (!cutiName.trim()) return;
    if (!getAccessToken()) {
      toast.error("Belum login.");
      return;
    }
    setCutiSaving(true);
    try {
      await saveRatePaidLeave(periodId, cutiName, {
        dates: [],
        reason: "",
        proof: "tidak terlampir",
      });
      await loadRates();
      toast.success("Cuti dihapus dari database");
      setCutiDeleteOpen(false);
      setCutiOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menghapus cuti");
    } finally {
      setCutiSaving(false);
    }
  };

  const generateSlip = (nameEmployee: string) => {
    const id = resolveEmployeeId(nameEmployee);
    if (!id) {
      toast.error("Nama tidak cocok dengan data karyawan.");
      return;
    }
    if (!payrollYm) return;
    setActivePayrollPeriod(payrollYm);
    const rateRow = rates.find((rr) => rr.name_employee.trim().toLowerCase() === nameEmployee.trim().toLowerCase());
    const p = calculatePayslip(id, {
      mergeRateIncentive: rateRow?.incentive,
      mergeRateTax: rateRow?.tax,
      rateReimburse: rateRow?.reimburse,
      rateAdditional: rateRow?.additional_hours,
      ratePaidLeave: rateRow?.paid_leave,
      periodStartDate: period?.start_date ?? null,
      periodEndDate: period?.end_date ?? null,
    });
    if (!p) {
      toast.error("Tidak dapat menghitung slip gaji untuk karyawan ini.");
      return;
    }
    downloadPayslipPDF(p);
    toast.success("Unduhan slip gaji dimulai");
  };

  const handleDownloadAllEarnExcel = () => {
    if (!period || rates.length === 0) {
      toast.error("Belum ada data rate untuk periode ini. Muat ulang atau impor Hivedesk terlebih dahulu.");
      return;
    }
    try {
      downloadAllEarnStyleXlsx({
        rates,
        fileBasename: title,
        sheetName: title,
        periodStartDate: period.start_date ?? null,
        periodEndDate: period.end_date ?? null,
      });
      toast.success(`File ${title}.xlsx sedang diunduh`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal membuat file Excel");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/periode">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Kembali
              </Link>
            </Button>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {range ?? "Memuat..."} · status{" "}
            <span className="font-medium text-foreground">{period?.status ?? "-"}</span>
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 gap-2 self-start sm:self-auto"
          disabled={!period || rates.length === 0 || ratesLoading}
          onClick={handleDownloadAllEarnExcel}
          title={
            rates.length === 0
              ? "Tidak ada baris rate untuk diekspor"
              : `Unduh ringkasan semua karyawan (${title}.xlsx)`
          }
        >
          <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
          Unduh Excel semua karyawan
        </Button>
      </div>

      {rates.length > 0 && (
        <Card className="overflow-hidden border-border/80 shadow-none">
          <CardHeader className="gap-4 space-y-0 border-b bg-gradient-to-br from-secondary/80 to-muted/40 pb-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-2 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-lg sm:text-xl font-semibold tracking-tight">Hasil ringkasan Hivedesk</CardTitle>
                  <span className="inline-flex items-center rounded-full border bg-background px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    Data dari server
                  </span>
                </div>
                <CardDescription className="text-sm leading-relaxed max-w-2xl">
                  Angka di bawah ini menjumlahkan semua baris pada periode ini. Per baris ada rincian per project serta aksi penggajian
                  setelah Anda membuka detail.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" className="shrink-0" disabled={ratesLoading} onClick={() => void loadRates()}>
                <RefreshCw className={`h-4 w-4 mr-2 ${ratesLoading ? "animate-spin" : ""}`} aria-hidden />
                {ratesLoading ? "Memuat ulang..." : "Muat ulang data"}
              </Button>
            </div>

            {/* Ringkasan agregat */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 pt-2">
              <div className="rounded-xl border border-border bg-card p-4 shadow-none ring-1 ring-border">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                    <Users className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Karyawan</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums">{ratesSummary.totalEmployees}</p>
                    <p className="mt-1 text-xs text-muted-foreground leading-snug">Orang dalam ringkasan periode ini</p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 shadow-none ring-1 ring-border">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                    <Clock className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Jam tercatat</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{ratesSummary.loggedTime}</p>
                    <p className="mt-1 text-xs text-muted-foreground leading-snug">Akumulasi waktu dicatat (logged)</p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 shadow-none ring-1 ring-border">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                    <Activity className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Jam aktif</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{ratesSummary.activeTime}</p>
                    <p className="mt-1 text-xs text-muted-foreground leading-snug">Akumulasi waktu aktivitas nyata</p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 shadow-none ring-1 ring-border">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                    <Wallet className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total biaya Hivedesk</p>
                    <p className="mt-1 text-xl font-semibold tabular-nums leading-snug break-words">
                      {formatIDR(ratesSummary.cost)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-snug">Gabungan nominal cost baris utama</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-gradient-to-b from-card via-card to-secondary/25 p-5 shadow-sm sm:p-6">
              <div className="flex flex-col gap-4 border-b border-border/70 pb-5 sm:flex-row sm:items-start sm:gap-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary ring-1 ring-border">
                  <Wallet className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <h3 className="text-base font-semibold tracking-tight text-foreground sm:text-lg">Total perolehan seluruh karyawan</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
                    Penjumlahan pendapatan Hivedesk + penambahan/pengurangan jam tambahan + reimburse + insentif − pajak (kolom rate), dijumlahkan
                    untuk semua baris karyawan pada periode ini.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
                <div className="flex min-h-[5.5rem] flex-col justify-between rounded-xl border border-border bg-card p-4 shadow-none transition-shadow hover:shadow-sm">
                  <div className="text-xs font-medium leading-snug text-muted-foreground">Pendapatan Hivedesk</div>
                  <div className="mt-2 text-base font-semibold tabular-nums tracking-tight text-foreground">{formatIDR(ratesSummary.cost)}</div>
                </div>
                <div className="flex min-h-[5.5rem] flex-col justify-between rounded-xl border border-border bg-card p-4 shadow-none transition-shadow hover:shadow-sm">
                  <div className="text-xs font-medium leading-snug text-muted-foreground">Penambahan / Pengurangan</div>
                  <div
                    className={cn(
                      "mt-2 text-base font-semibold tabular-nums tracking-tight",
                      ratesSummary.additionalEarn < 0 ? "text-destructive" : "text-foreground",
                    )}
                  >
                    {ratesSummary.additionalEarn >= 0 ? "+ " : "- "}
                    {formatIDR(Math.abs(ratesSummary.additionalEarn))}
                  </div>
                </div>
                <div className="flex min-h-[5.5rem] flex-col justify-between rounded-xl border border-border bg-card p-4 shadow-none transition-shadow hover:shadow-sm">
                  <div className="text-xs font-medium leading-snug text-muted-foreground">Reimburse</div>
                  <div className="mt-2 text-base font-semibold tabular-nums tracking-tight text-foreground">+ {formatIDR(ratesSummary.reimburseTotal)}</div>
                </div>
                <div className="flex min-h-[5.5rem] flex-col justify-between rounded-xl border border-border bg-card p-4 shadow-none transition-shadow hover:shadow-sm">
                  <div className="text-xs font-medium leading-snug text-muted-foreground">Insentif</div>
                  <div className="mt-2 text-base font-semibold tabular-nums tracking-tight text-foreground">+ {formatIDR(ratesSummary.incentiveTotal)}</div>
                </div>
                <div className="flex min-h-[5.5rem] flex-col justify-between rounded-xl border border-border bg-card p-4 shadow-none transition-shadow hover:shadow-sm">
                  <div className="text-xs font-medium leading-snug text-muted-foreground">Pajak</div>
                  <div className="mt-2 text-base font-semibold tabular-nums tracking-tight text-destructive">
                    {ratesSummary.taxTotal > 0 ? `- ${formatIDR(ratesSummary.taxTotal)}` : formatIDR(0)}
                  </div>
                </div>
                <div className="flex min-h-[5.5rem] flex-col justify-between rounded-xl border border-border bg-card p-4 shadow-none transition-shadow hover:shadow-sm sm:col-span-2 md:col-span-1 xl:col-span-1">
                  <div className="text-xs font-medium leading-snug text-muted-foreground">
                    Cuti berbayar{ratesSummary.paidLeaveDays > 0 ? ` · ${ratesSummary.paidLeaveDays} hari` : ""}
                  </div>
                  <div className="mt-2 text-base font-semibold tabular-nums tracking-tight text-foreground">+ {formatIDR(ratesSummary.paidLeaveEarn)}</div>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-2 rounded-xl bg-primary px-5 py-4 text-primary-foreground sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <span className="text-sm font-medium tracking-wide text-primary-foreground/90">Grand total</span>
                <span className="text-2xl font-bold tabular-nums tracking-tight sm:text-3xl">{formatIDR(ratesSummary.grandEarn)}</span>
              </div>
            </div>

            <div className="rounded-xl border bg-muted/30 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background shadow-sm ring-1 ring-border">
                    <Percent className="h-4 w-4 text-muted-foreground" aria-hidden />
                  </span>
                  <div>
                    <p className="text-sm font-medium">Tingkat aktivitas agregat</p>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                      Perbandingan jam aktif terhadap jam tercatat untuk seluruh karyawan; makin mendekati 100% makin besar porsi waktu aktif dari
                      waktu yang di-log.
                    </p>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <span className="text-2xl font-bold tabular-nums">{ratesSummary.activityPct}%</span>
                </div>
              </div>
              <Progress value={Math.min(100, Math.max(0, ratesSummary.activityPct))} className="mt-3 h-2" aria-label={`Aktivitas ${ratesSummary.activityPct}%`} />
            </div>
          </CardHeader>

          <CardContent className="space-y-8 px-4 py-6 sm:px-8">
            <header className="relative overflow-hidden rounded-2xl border border-border/70 bg-muted/25 px-5 py-6 sm:px-7">
              <div
                className="pointer-events-none absolute -right-8 -top-12 h-40 w-40 rounded-full bg-primary/10 blur-3xl"
                aria-hidden
              />
              <h2 className="text-lg font-semibold tracking-tight">Daftar karyawan</h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                Satu kartu untuk satu nama. Nilai utama di kanan adalah biaya dari Hivedesk. Di bawahnya ada tarif dan jam secara ringkas — buka{" "}
                <span className="font-medium text-foreground">Detail</span> untuk pembagian project, reimburse (kolom rate), jam tambahan dari notes,
                serta insentif / pajak / cuti / slip gaji.
              </p>
            </header>

            <div className="space-y-5">
              {rates.map((r, idx) => {
                const details = safeDetailsProject(r.details_project);
                const totals = details.totals || {};
                const projects = details.projects || [];
                const isDetailRow = expandedEmployee === r.name_employee;
                const isDetailPanelExpanded = isDetailRow && !detailCollapsed;
                const add = safeAdditionalHoursPayload(r.additional_hours ?? null);
                const reimb = safeReimbursePayload(r.reimburse ?? null);
                const reimbNameMismatch =
                  !!reimb?.name_employee &&
                  reimb.name_employee.trim().toLowerCase() !== r.name_employee.trim().toLowerCase();
                const paidLeave = safePaidLeavePayload(r.paid_leave ?? null);
                const cutiAdj = computeCutiAdjustedTotalsForRateRow(r);
                const addSecondsRaw =
                  add?.details_additing && add.details_additing.length > 0
                    ? add.details_additing.reduce((acc, it) => acc + hhDotMmToSeconds(it?.total_hours), 0)
                    : hhDotMmToSeconds(add?.total_hours_additing);
                const addSeconds = cutiAdj.adjustedAddSeconds;
                const addEarn = cutiAdj.adjustedAddEarn;
                const baseSeconds = cutiAdj.adjustedLoggedSeconds;
                const combinedSeconds = baseSeconds + addSeconds;
                const baseEarn = cutiAdj.effectiveBaseEarn;
                /** Total reimburse: utamakan field `total_reimburse`; fallback dijumlahkan dari `details_reimburse`. */
                const reimbAmount = (() => {
                  if (!reimb) return 0;
                  if (typeof reimb.total_reimburse === "number" && Number.isFinite(reimb.total_reimburse)) return reimb.total_reimburse;
                  return (reimb.details_reimburse ?? []).reduce(
                    (acc, d) => acc + (typeof d?.reimburse === "number" && Number.isFinite(d.reimburse) ? d.reimburse : 0),
                    0,
                  );
                })();
                const incAmount = incentiveTotalFromRate(r.incentive);
                const incentiveItems = parseRateIncentiveItems(r.incentive);
                const taxAmount = taxTotalFromRate(r.tax);
                const taxItems = parseRateTaxItems(r.tax);
                const paidLeaveAmount =
                  paidLeave && typeof r.rate_hours === "number" && Number.isFinite(r.rate_hours)
                    ? paidLeave.total_hours * r.rate_hours
                    : 0;
                /** Grand-total: Pendapatan Hivedesk + Penambahan/Pengurangan jam + Reimburse + Insentif + Cuti berbayar − Pajak. */
                const grandEarn =
                  baseEarn == null && addEarn == null && reimbAmount === 0 && incAmount === 0 && paidLeaveAmount === 0 && taxAmount === 0
                    ? null
                    : (baseEarn ?? 0) + (addEarn ?? 0) + reimbAmount + incAmount + paidLeaveAmount - taxAmount;
                const hasGrandBreakdown =
                  baseEarn != null || addEarn != null || reimbAmount !== 0 || incAmount !== 0 || paidLeaveAmount !== 0 || taxAmount !== 0;
                const rowActivity = cutiAdj.adjustedActivityPct;
                const projectCount = projects.length;
                const matchedMaster = !!resolveEmployeeId(r.name_employee);
                const statClass =
                  "rounded-xl border border-border bg-card px-3 py-2.5 shadow-none transition-colors hover:border-primary";

                return (
                  <article
                    key={r.id}
                    className={cn(
                      "overflow-hidden rounded-2xl border bg-card shadow-none transition-[box-shadow,border-color,transform] duration-200",
                      isDetailRow ? "border-primary shadow-md" : "border-border hover:border-primary hover:shadow-sm",
                    )}
                  >
                    <div
                      className={cn("h-1.5 bg-gradient-to-r", CARD_ACCENT_BAR[idx % CARD_ACCENT_BAR.length])}
                      aria-hidden
                    />
                    <div className="p-5 sm:p-6">
                      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex min-w-0 gap-4">
                          <div
                            className={cn(
                              "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-base font-bold tracking-tight text-white shadow-inner ring-2 ring-white/20",
                              "bg-gradient-to-br",
                              CARD_ACCENT_BAR[idx % CARD_ACCENT_BAR.length],
                            )}
                            aria-hidden
                          >
                            {employeeInitials(r.name_employee)}
                          </div>
                          <div className="min-w-0 space-y-2">
                            <div>
                              <h3 className="truncate text-lg font-semibold leading-tight tracking-tight sm:text-xl">
                                {r.name_employee}
                              </h3>
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Badge variant="secondary" className="font-normal tabular-nums">
                                  {projectCount} proyek
                                </Badge>
                                {matchedMaster ? (
                                  <Badge variant="outline" className="border-primary bg-secondary text-primary font-normal">
                                    Nama cocok dengan data Karyawan
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="border-border bg-warning text-warning-foreground font-normal">
                                    Samakan nama di menu Karyawan untuk insentif & slip
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end lg:flex-col lg:items-end xl:flex-row xl:items-center">
                          <Button
                            type="button"
                            size="sm"
                            variant={isDetailPanelExpanded ? "secondary" : "default"}
                            className="w-full shrink-0 gap-1.5 sm:w-auto lg:order-2"
                            onClick={() => toggleEmployeeDetail(r.name_employee)}
                            aria-expanded={isDetailRow}
                            disabled={isDetailRow && detailCollapsed}
                          >
                            {isDetailRow ? (
                              <>
                                <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                                Tutup detail
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                                Lihat detail
                              </>
                            )}
                          </Button>
                          <div className="text-left sm:text-right lg:order-1">
                            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Biaya Hivedesk</p>
                            <p className="mt-0.5 text-2xl font-bold tabular-nums tracking-tight text-primary">
                              {baseEarn != null ? formatIDR(baseEarn) : "—"}
                            </p>
                            {cutiAdj.hasSuppression ? (
                              <p className="mt-1 max-w-[14rem] text-left text-[10px] leading-snug text-muted-foreground sm:ml-auto sm:max-w-xs sm:text-right">
                                Sesi atau jam tambahan pada hari cuti (tanpa wajib kerja): bagian dalam rentang lokal{" "}
                                <span className="font-medium text-foreground">08:00–16:01</span> tidak masuk total di atas.
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <div className={statClass}>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Tarif / jam</p>
                          <p className="mt-1 truncate text-sm font-semibold tabular-nums">
                            {typeof r.rate_hours === "number" ? formatIDR(r.rate_hours) : "—"}
                          </p>
                        </div>
                        <div className={statClass}>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Jam tercatat</p>
                          <p className="mt-1 font-mono text-sm font-medium tabular-nums text-foreground">{secondsToHms(baseSeconds)}</p>
                        </div>
                        <div className={statClass}>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Jam aktif</p>
                          <p className="mt-1 font-mono text-sm font-medium tabular-nums text-foreground">
                            {secondsToHms(cutiAdj.adjustedActiveSeconds)}
                          </p>
                        </div>
                        <div className={cn(statClass, "col-span-2 md:col-span-1")}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Aktivitas</p>
                            <span className="text-sm font-bold tabular-nums">{rowActivity != null ? `${rowActivity}%` : "—"}</span>
                          </div>
                          {rowActivity != null ? (
                            <Progress
                              value={Math.min(100, Math.max(0, rowActivity))}
                              className="mt-2 h-2"
                              aria-label={`Aktivitas ${rowActivity} persen`}
                            />
                          ) : (
                            <p className="mt-2 text-xs text-muted-foreground">Tidak ada data aktivitas untuk baris ini.</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {isDetailRow && (
                      <div
                        className={cn(
                          "grid overflow-hidden transition-[grid-template-rows,opacity] ease-out motion-reduce:transition-none",
                          detailCollapsed ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100",
                        )}
                        style={{ transitionDuration: `${EMPLOYEE_DETAIL_MOTION_MS}ms` }}
                      >
                        <div className="min-h-0">
                          <div
                            className={cn(
                              "space-y-6 border-t border-border/65 bg-gradient-to-b from-muted/45 via-muted/15 to-background px-5 py-6 sm:px-7 sm:py-8 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-300",
                              detailCollapsed && "pointer-events-none",
                            )}
                          >
                        <div
                          id={`rate-detail-heading-${r.id}`}
                          className="flex scroll-mt-24 flex-col gap-3 border-b border-border/50 pb-5 sm:scroll-mt-28 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-background shadow-sm ring-1 ring-border">
                              <Layers className="h-5 w-5 text-primary" aria-hidden />
                            </span>
                            <div>
                              <h4 className="text-base font-semibold tracking-tight">Rincian penggajian</h4>
                              <p className="text-sm text-muted-foreground">{r.name_employee}</p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Per project</p>
                          <div className="overflow-x-auto rounded-xl border border-border/70 bg-background shadow-inner">
                                  <table className="w-full text-sm min-w-[740px]">
                                    <thead className="bg-muted/50 [&_th]:text-muted-foreground [&_th]:font-medium border-b border-border/60">
                                      <tr className="text-left text-xs sm:text-sm">
                                        <th className="px-3 py-2.5">Project</th>
                                        <th className="px-3 py-2.5">Sesi kerja</th>
                                        <th className="px-3 py-2.5 text-right">Jam tercatat</th>
                                        <th className="px-3 py-2.5 text-right">Jam aktif</th>
                                        <th className="px-3 py-2.5 text-right">Aktivitas</th>
                                        <th className="px-3 py-2.5 text-right">Biaya</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(projects ?? []).map((p: any, idx: number) => (
                                        <tr key={`${p.project}-${idx}`} className="border-t">
                                          <td className="px-3 py-2 font-medium">{p.project}</td>
                                          <td className="px-3 py-2 text-muted-foreground">{p.workSessionType ?? "-"}</td>
                                          <td className="px-3 py-2 text-right tabular-nums">{p.loggedTime}</td>
                                          <td className="px-3 py-2 text-right tabular-nums">{p.activeTime}</td>
                                          <td className="px-3 py-2 text-right tabular-nums">{p.activityPct}%</td>
                                          <td className="px-3 py-2 text-right tabular-nums">{formatCostIdr(p.cost)}</td>
                                        </tr>
                                      ))}
                                      {(projects ?? []).length > 0 && (
                                        <tr className="border-t bg-muted/40 font-semibold">
                                          <td className="px-3 py-2" colSpan={2}>
                                            Total (setelah cuti)
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums">{secondsToHms(cutiAdj.adjustedLoggedSeconds)}</td>
                                          <td className="px-3 py-2 text-right tabular-nums">{secondsToHms(cutiAdj.adjustedActiveSeconds)}</td>
                                          <td className="px-3 py-2 text-right tabular-nums">
                                            {cutiAdj.adjustedActivityPct != null ? `${cutiAdj.adjustedActivityPct}%` : "—"}
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums">
                                            {baseEarn != null ? formatIDR(baseEarn) : "—"}
                                          </td>
                                        </tr>
                                      )}
                                      {(projects ?? []).length === 0 && (
                                        <tr>
                                          <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                                            Tidak ada project untuk karyawan ini.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                          </div>
                        </div>

                        <SessionsByProject projects={projects} report={details.report} paidLeave={paidLeave} />

                        <div className="space-y-2 pt-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Reimburse (kolom rate, sinkron notes)
                          </p>
                          <div className="rounded-xl border border-border bg-secondary p-4 shadow-none">
                            {!reimb || (reimb.details_reimburse?.length ?? 0) === 0 ? (
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                Belum ada reimburse tersimpan untuk{" "}
                                <span className="font-medium text-foreground">{r.name_employee}</span> pada periode ini (atau belum di-sync dari
                                notes).
                              </p>
                            ) : (
                              <>
                                {reimbNameMismatch ? (
                                  <p className="mb-3 rounded-md border border-border bg-warning px-3 py-2 text-xs text-warning-foreground">
                                    Nama di JSON reimburse ({reimb.name_employee}) tidak sama dengan nama baris rate ({r.name_employee}). Periksa
                                    konsistensi nama di data reimburse.
                                  </p>
                                ) : null}
                                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                                  <div>
                                    <div className="text-xs text-muted-foreground">Total reimburse</div>
                                    <div className="font-semibold tabular-nums text-foreground">
                                      {typeof reimb.total_reimburse === "number" ? formatIDR(reimb.total_reimburse) : "—"}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-muted-foreground">Jumlah entri</div>
                                    <div className="font-semibold tabular-nums text-foreground">
                                      {reimb.total_count ?? reimb.details_reimburse?.length ?? 0}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-3 overflow-x-auto rounded-md border border-border bg-background">
                                  <table className="w-full min-w-[560px] text-sm">
                                    <thead className="bg-muted [&_th]:font-medium [&_th]:text-foreground">
                                      <tr className="text-left">
                                        <th className="px-3 py-2">Tanggal</th>
                                        <th className="px-3 py-2">Deskripsi</th>
                                        <th className="px-3 py-2 text-right">Nominal</th>
                                        <th className="px-3 py-2">Bukti</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(reimb.details_reimburse ?? []).map((d, ri) => (
                                        <tr key={`${d?.date_add ?? "na"}-${ri}`} className="border-t">
                                          <td className="px-3 py-2 tabular-nums">
                                            {formatDateDmyFromYmd(d?.date_add ?? null, "—")}
                                          </td>
                                          <td className="px-3 py-2">{String(d?.description ?? "—")}</td>
                                          <td className="px-3 py-2 text-right font-medium tabular-nums text-foreground">
                                            {typeof d?.reimburse === "number" && Number.isFinite(d.reimburse) ? formatIDR(d.reimburse) : "—"}
                                          </td>
                                          <td className="px-3 py-2 text-muted-foreground">{String(d?.proof ?? "—")}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2 pt-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Insentif (kolom rate, JSONB)
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="h-8"
                              onClick={() => openInsentifForEmployee(r.name_employee)}
                            >
                              <PlusSquare className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                              Tambah insentif
                            </Button>
                          </div>
                          <div className="rounded-xl border border-border bg-muted p-4 shadow-none">
                            {incentiveItems.length === 0 ? (
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                Belum ada insentif untuk{" "}
                                <span className="font-medium text-foreground">{r.name_employee}</span>. Klik Tambah insentif untuk menyimpan ke
                                database.
                              </p>
                            ) : (
                              <>
                                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 mb-3">
                                  <div>
                                    <div className="text-xs text-muted-foreground">Total insentif</div>
                                    <div className="font-semibold tabular-nums text-foreground">{formatIDR(incAmount)}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-muted-foreground">Jumlah entri</div>
                                    <div className="font-semibold tabular-nums text-foreground">{incentiveItems.length}</div>
                                  </div>
                                </div>
                                <div className="overflow-x-auto rounded-md border border-border bg-background">
                                  <table className="w-full min-w-[520px] text-sm">
                                    <thead className="bg-muted [&_th]:font-medium [&_th]:text-foreground">
                                      <tr className="text-left">
                                        <th className="px-3 py-2">Jenis</th>
                                        <th className="px-3 py-2 text-right">Nominal</th>
                                        <th className="px-3 py-2">Catatan</th>
                                        <th className="px-3 py-2 text-right w-[100px]">Aksi</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {incentiveItems.map((it) => (
                                        <tr key={it.id} className="border-t">
                                          <td className="px-3 py-2">
                                            {INSENTIF_TYPES.find((t) => t.key === it.jenis)?.label ?? it.jenis}
                                          </td>
                                          <td className="px-3 py-2 text-right font-medium tabular-nums text-foreground">
                                            {formatIDR(it.nominal)}
                                          </td>
                                          <td className="px-3 py-2 text-muted-foreground">{it.catatan ?? "—"}</td>
                                          <td className="px-3 py-2 text-right">
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 px-2"
                                              onClick={() => openEditInsentif(r.name_employee, it)}
                                            >
                                              <Pencil className="h-3.5 w-3.5" aria-hidden />
                                            </Button>
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 px-2 text-destructive"
                                              onClick={() => setInsDeleteTarget({ name: r.name_employee, id: it.id })}
                                            >
                                              <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2 pt-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Pajak (kolom rate.tax, JSONB)
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="h-8"
                              onClick={() => openPajakForEmployee(r.name_employee)}
                            >
                              <Landmark className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                              Tambah pajak
                            </Button>
                          </div>
                          <div className="rounded-xl border border-border bg-muted p-4 shadow-none">
                            {taxItems.length === 0 ? (
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                Belum ada pajak untuk{" "}
                                <span className="font-medium text-foreground">{r.name_employee}</span>. Klik Tambah pajak untuk menyimpan ke
                                database. Pajak mengurangi total perolehan.
                              </p>
                            ) : (
                              <>
                                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 mb-3">
                                  <div>
                                    <div className="text-xs text-muted-foreground">Total pajak</div>
                                    <div className="font-semibold tabular-nums text-destructive">{formatIDR(taxAmount)}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-muted-foreground">Jumlah entri</div>
                                    <div className="font-semibold tabular-nums text-foreground">{taxItems.length}</div>
                                  </div>
                                </div>
                                <div className="overflow-x-auto rounded-md border border-border bg-background">
                                  <table className="w-full min-w-[520px] text-sm">
                                    <thead className="bg-muted [&_th]:font-medium [&_th]:text-foreground">
                                      <tr className="text-left">
                                        <th className="px-3 py-2">Jenis</th>
                                        <th className="px-3 py-2 text-right">Nominal</th>
                                        <th className="px-3 py-2">Catatan</th>
                                        <th className="px-3 py-2 text-right w-[100px]">Aksi</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {taxItems.map((it) => (
                                        <tr key={it.id} className="border-t">
                                          <td className="px-3 py-2">
                                            {PAJAK_TYPES.find((t) => t.key === it.jenis)?.label ?? it.jenis}
                                          </td>
                                          <td className="px-3 py-2 text-right font-medium tabular-nums text-destructive">
                                            − {formatIDR(it.nominal)}
                                          </td>
                                          <td className="px-3 py-2 text-muted-foreground">{it.catatan ?? "—"}</td>
                                          <td className="px-3 py-2 text-right">
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 px-2"
                                              onClick={() => openEditPajak(r.name_employee, it)}
                                            >
                                              <Pencil className="h-3.5 w-3.5" aria-hidden />
                                            </Button>
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 px-2 text-destructive"
                                              onClick={() => setTaxDeleteTarget({ name: r.name_employee, id: it.id })}
                                            >
                                              <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2 pt-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Cuti berbayar (kolom rate.paid_leave, JSONB)
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="h-8"
                              onClick={() => openCutiForEmployee(r.name_employee)}
                            >
                              <Palmtree className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                              {paidLeave ? "Ubah cuti" : "Tambah cuti"}
                            </Button>
                          </div>
                          <div className="rounded-xl border border-border bg-secondary p-4 shadow-none">
                            {!paidLeave ? (
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                Belum ada cuti berbayar untuk{" "}
                                <span className="font-medium text-foreground">{r.name_employee}</span>. Klik Tambah cuti untuk memilih
                                tanggal, alasan, dan bukti.
                              </p>
                            ) : (
                              <>
                                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-4 mb-3">
                                  <div>
                                    <div className="text-xs text-muted-foreground">Total hari</div>
                                    <div className="font-semibold tabular-nums text-foreground">{paidLeave.total_days} hari</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-muted-foreground">Total jam ({paidLeave.hours_per_day} jam/hari)</div>
                                    <div className="font-semibold tabular-nums text-foreground">{paidLeave.total_hours} jam</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-muted-foreground">Nominal cuti</div>
                                    <div className="font-semibold tabular-nums text-foreground">
                                      {paidLeaveAmount === 0 ? "-" : formatIDR(paidLeaveAmount)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-muted-foreground">Bukti</div>
                                    <div className="font-semibold text-foreground capitalize">{paidLeave.proof}</div>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <div className="text-xs text-muted-foreground">Tanggal cuti</div>
                                  <div className="flex flex-wrap gap-1">
                                    {paidLeave.dates.map((d, i) => {
                                      const workReq = (paidLeave.work_required_dates ?? []).includes(d);
                                      return (
                                        <span
                                          key={`${d}-${i}`}
                                          className="inline-flex items-center gap-0.5 rounded-full bg-background px-2 py-0.5 text-[11px] ring-1 ring-inset ring-border"
                                        >
                                          <span className="tabular-nums">{formatDateDmyFromYmd(d)}</span>
                                          {workReq ? (
                                            <span className="text-[12px] leading-none" title="Wajib kerja (True)" aria-hidden>
                                              🔥
                                            </span>
                                          ) : null}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                                {paidLeave.reason ? (
                                  <div className="mt-3 rounded-md border border-border bg-background/80 p-3 text-sm">
                                    <div className="text-xs text-muted-foreground mb-1">Alasan cuti</div>
                                    <p className="text-foreground/90 leading-relaxed">{paidLeave.reason}</p>
                                  </div>
                                ) : null}
                              </>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2 pt-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Jam tambahan (notes / backend)
                          </p>
                          {(r.additional_hours == null || add) && (
                            <div className="rounded-xl border border-border bg-secondary p-4 shadow-none">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-foreground">Sinkron tambahan jam</div>
                                    {r.additional_hours == null ? (
                                      <div className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                        Belum ada entri tambahan untuk baris ini.
                                      </div>
                                    ) : !add ? null : (
                                      <>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2 text-sm">
                                          <div>
                                            <div className="text-xs text-muted-foreground">Total Jam Penambahan (dihitung)</div>
                                            <div className="font-semibold text-foreground">{secondsToHms(addSeconds)}</div>
                                            {addSecondsRaw > addSeconds ? (
                                              <p className="mt-1 text-[11px] leading-snug text-destructive">
                                                Tercatat mentah {secondsToHms(addSecondsRaw)} — {secondsToHms(addSecondsRaw - addSeconds)} tidak
                                                dihitung (overlap rentang cuti lokal 08:00–16:01 tanpa wajib kerja).
                                              </p>
                                            ) : null}
                                          </div>
                                          <div>
                                            <div className="text-xs text-muted-foreground">Nilai Penambahan</div>
                                            <div className="font-semibold text-foreground">
                                              {addEarn == null ? "-" : formatIDR(addEarn)}
                                            </div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-muted-foreground">Rate/Jam</div>
                                            <div className="font-semibold text-foreground">
                                              {typeof r.rate_hours === "number" ? formatIDR(r.rate_hours) : "-"}
                                            </div>
                                          </div>
                                        </div>
                                      </>
                                    )}

                                    {add && (add.details_additing ?? []).length > 0 && (
                                      <div className="overflow-x-auto rounded-md border border-border bg-background mt-3">
                                        <table className="w-full text-sm min-w-[920px]">
                                          <thead className="bg-muted">
                                            <tr className="text-left text-foreground">
                                              <th className="px-3 py-2">Status</th>
                                              <th className="px-3 py-2">Tanggal</th>
                                              <th className="px-3 py-2">Project</th>
                                              <th className="px-3 py-2">Reason</th>
                                              <th className="px-3 py-2 text-right">Mulai</th>
                                              <th className="px-3 py-2 text-right">Selesai</th>
                                              <th className="px-3 py-2 text-right">Total</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(add.details_additing ?? []).map((d, idx) => {
                                              const rowSec = hhDotMmToSeconds(d?.total_hours);
                                              const hang = cutiHangusEffectForAdditionalRow(paidLeave, d, rowSec);
                                              const fullHangus = hang.applicable && hang.voidRatio >= 1;
                                              const partialHangus = hang.applicable && hang.voidRatio > 0 && hang.voidRatio < 1;
                                              return (
                                                <tr
                                                  key={`${d?.date_add ?? "na"}-${idx}`}
                                                  className={cn(
                                                    "border-t",
                                                    fullHangus &&
                                                      "bg-destructive/10 text-destructive [&_td]:text-destructive",
                                                    partialHangus &&
                                                      "bg-orange-50/90 text-foreground dark:bg-orange-950/30 [&_td]:text-foreground",
                                                  )}
                                                >
                                                  <td className="px-3 py-2 text-foreground font-medium">{String(d?.status ?? "-")}</td>
                                                  <td className="px-3 py-2.5 align-top tabular-nums">
                                                    <CutiHangusDateCell
                                                      dateLine={formatDateDmyFromYmd(d?.date_add ?? null)}
                                                      fullHangus={fullHangus}
                                                      partialHangus={partialHangus}
                                                      partialCountedLabel={hang.meta?.countedSegmentsLabel ?? null}
                                                    />
                                                  </td>
                                                  <td className="px-3 py-2">{String(d?.project ?? "-")}</td>
                                                  <td className="px-3 py-2">{String(d?.reason ?? "-")}</td>
                                                  <td className="px-3 py-2 text-right tabular-nums">
                                                    {formatClockLikeTo24h(d?.start_hours ?? null)}
                                                  </td>
                                                  <td className="px-3 py-2 text-right tabular-nums">
                                                    {formatClockLikeTo24h(d?.end_hours ?? null)}
                                                  </td>
                                                  <td className="px-3 py-2 text-right tabular-nums text-foreground font-semibold">
                                                    {secondsToHms(rowSec)}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )}

                        {hasGrandBreakdown && (
                                  <Card className="mt-2 border-border shadow-none">
                                    <CardHeader className="space-y-2 px-4 py-4 sm:px-6 sm:py-5">
                                      <CardTitle className="text-base font-semibold leading-snug tracking-tight text-primary sm:text-lg">
                                        Total perolehan karyawan
                                      </CardTitle>
                                      <CardDescription className="text-xs leading-relaxed sm:text-sm">
                                        Pendapatan Hivedesk, penambahan atau pengurangan jam tambahan, reimburse, insentif, pajak, dan cuti berbayar pada
                                        baris rate ini.
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4 px-4 pb-5 pt-0 sm:px-6">
                                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
                                        <div className="min-w-0 shrink-0">
                                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Jumlah</p>
                                          <p className="mt-1 text-3xl font-bold tracking-tight text-primary tabular-nums break-words sm:text-4xl">
                                            {grandEarn == null ? "-" : formatIDR(grandEarn)}
                                          </p>
                                        </div>
                                        <p className="min-w-0 max-w-none text-xs leading-relaxed text-muted-foreground lg:max-w-md lg:pt-1 lg:text-right">
                                          Ringkasan komponen di bawah menjumlahkan ke nilai ini. Total jam menggabungkan jam tercatat Hivedesk dan
                                          penyesuaian dari jam tambahan.
                                        </p>
                                      </div>

                                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                        <div className="flex min-h-[5.5rem] min-w-0 flex-col justify-between rounded-md border border-border bg-secondary p-3">
                                          <div className="text-xs font-medium leading-snug text-muted-foreground break-words">
                                            Pendapatan Hivedesk
                                          </div>
                                          <div className="mt-2 font-semibold tabular-nums text-foreground">
                                            {baseEarn == null ? "-" : formatIDR(baseEarn)}
                                          </div>
                                          <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                            Jam tercatat: {secondsToHms(baseSeconds)}
                                          </div>
                                        </div>
                                        <div className="flex min-h-[5.5rem] min-w-0 flex-col justify-between rounded-md border border-border bg-secondary p-3">
                                          <div className="text-xs font-medium leading-snug text-muted-foreground break-words">
                                            Penambahan / pengurangan jam
                                          </div>
                                          <div className={cn(
                                            "mt-2 font-semibold tabular-nums",
                                            (addEarn ?? 0) < 0 ? "text-destructive" : "text-foreground",
                                          )}>
                                            {addEarn == null ? "-" : `${addEarn >= 0 ? "+ " : "- "}${formatIDR(Math.abs(addEarn))}`}
                                          </div>
                                          <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                            Jam tambahan: {secondsToHms(addSeconds)}
                                          </div>
                                        </div>
                                        <div className="flex min-h-[5.5rem] min-w-0 flex-col justify-between rounded-md border border-border bg-secondary p-3">
                                          <div className="text-xs font-medium leading-snug text-muted-foreground break-words">Reimburse</div>
                                          <div className="mt-2 font-semibold tabular-nums text-foreground">
                                            {reimbAmount === 0 && !reimb ? "-" : `+ ${formatIDR(reimbAmount)}`}
                                          </div>
                                          <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                            {reimb && (reimb.total_count ?? reimb.details_reimburse?.length ?? 0) > 0
                                              ? `${reimb.total_count ?? reimb.details_reimburse?.length} entri`
                                              : "Tidak ada entri"}
                                          </div>
                                        </div>
                                        <div className="flex min-h-[5.5rem] min-w-0 flex-col justify-between rounded-md border border-border bg-secondary p-3">
                                          <div className="text-xs font-medium leading-snug text-muted-foreground break-words">Insentif</div>
                                          <div className="mt-2 font-semibold tabular-nums text-foreground">
                                            {incAmount === 0 ? "-" : `+ ${formatIDR(incAmount)}`}
                                          </div>
                                          <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                            {incentiveItems.length > 0 ? `${incentiveItems.length} entri` : "Tidak ada entri"}
                                          </div>
                                        </div>
                                        <div className="flex min-h-[5.5rem] min-w-0 flex-col justify-between rounded-md border border-border bg-secondary p-3">
                                          <div className="text-xs font-medium leading-snug text-muted-foreground break-words">Pajak</div>
                                          <div className="mt-2 font-semibold tabular-nums text-destructive">
                                            {taxAmount === 0 ? "-" : `- ${formatIDR(taxAmount)}`}
                                          </div>
                                          <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                            {taxItems.length > 0 ? `${taxItems.length} entri` : "Tidak ada entri"}
                                          </div>
                                        </div>
                                        <div className="flex min-h-[5.5rem] min-w-0 flex-col justify-between rounded-md border border-border bg-secondary p-3">
                                          <div className="text-xs font-medium leading-snug text-muted-foreground break-words">Cuti berbayar</div>
                                          <div className="mt-2 font-semibold tabular-nums text-foreground">
                                            {paidLeaveAmount === 0 ? "-" : `+ ${formatIDR(paidLeaveAmount)}`}
                                          </div>
                                          <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                            {paidLeave
                                              ? `${paidLeave.total_days} hari · ${paidLeave.total_hours} jam`
                                              : "Tidak ada cuti"}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex flex-col gap-1 rounded-lg border border-dashed border-primary/25 bg-primary/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                        <p className="text-xs font-medium text-muted-foreground">Total jam gabungan</p>
                                        <p className="font-mono text-sm font-semibold tabular-nums text-foreground">
                                          {secondsToHms(combinedSeconds)}
                                        </p>
                                      </div>

                                      <div className="rounded-md border border-border bg-muted/30 p-3">
                                        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Rumus</p>
                                        <p className="text-xs leading-relaxed text-foreground">
                                          <span className="font-mono break-words [overflow-wrap:anywhere]">
                                            {baseEarn == null ? "—" : formatIDR(baseEarn)}{" "}
                                            {addEarn != null && addEarn < 0 ? "-" : "+"}{" "}
                                            {addEarn == null ? formatIDR(0) : formatIDR(Math.abs(addEarn))} + {formatIDR(reimbAmount)} +{" "}
                                            {formatIDR(incAmount)} + {formatIDR(paidLeaveAmount)}
                                            {taxAmount > 0 ? ` − ${formatIDR(taxAmount)}` : ""} ={" "}
                                            <span className="font-semibold text-primary">{grandEarn == null ? "—" : formatIDR(grandEarn)}</span>
                                          </span>
                                        </p>
                                      </div>
                                    </CardContent>
                                  </Card>
                                )}

                        {r.additional_hours != null && !add && (
                          <div className="mt-2 rounded-xl border border-border bg-warning p-4 text-sm text-warning-foreground">
                            <p className="font-medium">Format jam tambahan tidak terbaca</p>
                            <p className="mt-1 opacity-90 leading-relaxed">
                              Data tambahan tersimpan tetapi tidak bisa diparse. Cek sumber sinkron atau hubungi administrator.
                            </p>
                          </div>
                        )}
                        </div>

                        <div className="border-t border-border/55 pt-6 mt-2">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Tindakan untuk karyawan ini
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button type="button" size="sm" onClick={() => generateSlip(r.name_employee)}>
                              <Download className="h-4 w-4 mr-2" aria-hidden />
                              Slip gaji (PDF)
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                    )}
                  </article>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-dashed bg-muted/20">
        <CardContent className="py-4 text-sm text-muted-foreground">
          Insentif, pajak, dan cuti berbayar diatur dari blok masing-masing pada detail karyawan (setelah «Lihat detail»), lalu tersimpan ke{" "}
          <code className="text-xs">rate.incentive</code>, <code className="text-xs">rate.tax</code>, dan <code className="text-xs">rate.paid_leave</code>{" "}
          (JSONB). Unduh slip lewat{" "}
          <span className="font-medium text-foreground">Slip gaji (PDF)</span> di bagian «Tindakan untuk karyawan ini». Reimburse mengikuti alur
          pembuatan periode di backend.
        </CardContent>
      </Card>

      <Dialog
        open={insOpen}
        onOpenChange={(o) => {
          setInsOpen(o);
          if (!o) {
            setInsDraft(emptyInsForm);
            setInsTargetName(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingIns ? "Edit" : "Tambah"} insentif</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Karyawan</Label>
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium">
                {insTargetName ?? "—"}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Data disimpan ke kolom <code className="text-xs">rate.incentive</code> (JSONB) untuk periode ini.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Jenis insentif</Label>
              <Select value={insDraft.type} onValueChange={(v) => setInsDraft({ ...insDraft, type: v as InsentifJenis })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSENTIF_TYPES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nominal insentif (IDR)</Label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">Rp</span>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="0"
                  className="pl-9 tabular-nums"
                  value={insDraft.amount > 0 ? formatRupiahDigits(insDraft.amount) : ""}
                  onChange={(e) => setInsDraft({ ...insDraft, amount: parseRupiahDigits(e.target.value) })}
                  onKeyDown={(e) => {
                    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End", "Tab"];
                    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
                    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                  }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Ketik angka saja; pemisah ribuan dan simbol Rp ditambahkan otomatis.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Catatan insentif (opsional)</Label>
              <Input value={insDraft.note || ""} onChange={(e) => setInsDraft({ ...insDraft, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setInsOpen(false)} disabled={insSaving}>
              Batal
            </Button>
            <Button type="button" onClick={() => void submitInsentif()} disabled={insSaving}>
              {insSaving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!insDeleteTarget} onOpenChange={(o) => !o && setInsDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus insentif?</AlertDialogTitle>
            <AlertDialogDescription>
              Entri insentif akan dihapus dari database untuk karyawan ini. Tindakan ini dapat dibatalkan dengan menambahkan kembali entri baru.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={insDeleteLoading}>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={insDeleteLoading}
              onClick={(e) => {
                e.preventDefault();
                void confirmDeleteInsentif();
              }}
            >
              {insDeleteLoading ? "Menghapus…" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={taxOpen}
        onOpenChange={(o) => {
          setTaxOpen(o);
          if (!o) {
            setTaxDraft(emptyTaxForm);
            setTaxTargetName(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTax ? "Edit" : "Tambah"} pajak</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Karyawan</Label>
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium">
                {taxTargetName ?? "—"}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Data disimpan ke kolom <code className="text-xs">rate.tax</code> (JSONB) untuk periode ini. Nominal pajak mengurangi total perolehan.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Jenis pajak</Label>
              <Select value={taxDraft.type} onValueChange={(v) => setTaxDraft({ ...taxDraft, type: v as PajakJenis })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAJAK_TYPES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nominal pajak (IDR)</Label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">Rp</span>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="0"
                  className="pl-9 tabular-nums"
                  value={taxDraft.amount > 0 ? formatRupiahDigits(taxDraft.amount) : ""}
                  onChange={(e) => setTaxDraft({ ...taxDraft, amount: parseRupiahDigits(e.target.value) })}
                  onKeyDown={(e) => {
                    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End", "Tab"];
                    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
                    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                  }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Ketik angka saja; pemisah ribuan dan simbol Rp ditambahkan otomatis. Nilai ini dikurangkan dari perolehan.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Catatan pajak (opsional)</Label>
              <Input value={taxDraft.note || ""} onChange={(e) => setTaxDraft({ ...taxDraft, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTaxOpen(false)} disabled={taxSaving}>
              Batal
            </Button>
            <Button type="button" onClick={() => void submitPajak()} disabled={taxSaving}>
              {taxSaving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!taxDeleteTarget} onOpenChange={(o) => !o && setTaxDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus pajak?</AlertDialogTitle>
            <AlertDialogDescription>
              Entri pajak akan dihapus dari database untuk karyawan ini. Tindakan ini dapat dibatalkan dengan menambahkan kembali entri baru.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={taxDeleteLoading}>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={taxDeleteLoading}
              onClick={(e) => {
                e.preventDefault();
                void confirmDeletePajak();
              }}
            >
              {taxDeleteLoading ? "Menghapus…" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={cutiOpen} onOpenChange={(o) => !cutiSaving && setCutiOpen(o)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{cutiEditing ? "Ubah cuti" : "Tambah cuti"} — {cutiName || "…"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Pilih tanggal cuti pada kalender. Untuk setiap tanggal, atur <span className="font-medium text-foreground">wajib kerja</span> (
              <span className="font-mono">True</span>
              ) atau tidak (<span className="font-mono">False</span>
              ). Jika <span className="font-mono">True</span>, tanggal tersebut ditandai dengan ikon api di kalender dan ringkasan. Disimpan di{" "}
              <code className="font-mono">rate.paid_leave.work_required_dates</code>.
            </p>

            <div className="grid gap-4 md:grid-cols-[minmax(320px,_1fr)_minmax(260px,_1fr)] md:items-start">
              <div className="rounded-lg border border-border/55 bg-card p-3 shadow-none">
                <Calendar
                  mode="multiple"
                  selected={cutiDates}
                  onSelect={(dates) => {
                    const next = Array.isArray(dates) ? dates : [];
                    const filtered = next.filter((d) => isDateWithinPeriod(d));
                    const allowed = new Set(filtered.map((d) => ymdLocal(d)));
                    setCutiWorkRequiredYmds((prev) => prev.filter((ymd) => allowed.has(ymd)));
                    setCutiDates(filtered);
                  }}
                  modifiers={cutiCalendarModifiers}
                  weekStartsOn={1}
                  numberOfMonths={1}
                  defaultMonth={periodStartDate ?? undefined}
                  startMonth={periodStartDate ?? undefined}
                  endMonth={periodEndDate ?? undefined}
                  disabled={
                    periodStartDate && periodEndDate
                      ? [{ before: periodStartDate }, { after: periodEndDate }]
                      : undefined
                  }
                  components={{ DayButton: CutiCalendarDayButton }}
                  className="mx-auto w-full max-w-[min(100%,22rem)] [--cell-size:2.75rem] p-1 sm:p-2"
                />
                {range ? (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Hanya tanggal dalam periode <span className="font-medium text-foreground">{range}</span> yang dapat dipilih.
                  </p>
                ) : null}
              </div>
              <div className="space-y-3">
                <div className="rounded-md border border-border/55 bg-muted/30 p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium uppercase tracking-wide text-muted-foreground">Total hari cuti</span>
                    <span className="text-base font-semibold">{cutiTotalDays} hari</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-muted-foreground">
                    <span>Estimasi jam ({cutiHpd > 0 ? cutiHpd : 8} jam/hari)</span>
                    <span>{cutiTotalHours} jam</span>
                  </div>
                  {cutiDates.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Wajib kerja per tanggal (True / False)
                      </p>
                      {[...cutiDates]
                        .sort((a, b) => a.getTime() - b.getTime())
                        .map((d, i) => {
                          const ymd = ymdLocal(d);
                          const required = cutiWorkRequiredSet.has(ymd);
                          return (
                            <div
                              key={`${ymd}-${i}`}
                              className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-background px-2 py-1.5 text-[11px]"
                            >
                              <span className="min-w-[5.5rem] tabular-nums font-medium text-foreground">{formatDateDmyFromDate(d)}</span>
                              <span className="rounded bg-muted px-1.5 py-px font-mono text-[10px] text-muted-foreground">
                                {required ? "True" : "False"}
                              </span>
                              {required ? (
                                <span className="text-sm leading-none" title="Wajib kerja" aria-hidden>
                                  🔥
                                </span>
                              ) : null}
                              <div className="ml-auto flex items-center gap-2">
                                <Label htmlFor={`cuti-wr-${ymd}`} className="cursor-pointer text-[10px] font-normal text-muted-foreground">
                                  Wajib kerja
                                </Label>
                                <Switch
                                  id={`cuti-wr-${ymd}`}
                                  checked={required}
                                  onCheckedChange={(v) => {
                                    setCutiWorkRequiredYmds((prev) => {
                                      if (v) return prev.includes(ymd) ? prev : [...prev, ymd].sort();
                                      return prev.filter((x) => x !== ymd);
                                    });
                                  }}
                                  disabled={cutiSaving}
                                  aria-label={`Wajib kerja pada ${formatDateDmyFromDate(d)}: ${required ? "ya" : "tidak"}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="mt-2 italic text-muted-foreground">Belum ada tanggal terpilih.</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="cuti-hpd" className="text-xs">
                    Jam kerja per hari
                  </Label>
                  <Input
                    id="cuti-hpd"
                    type="number"
                    min={0.5}
                    step={0.5}
                    value={cutiHoursPerDay}
                    onChange={(e) => setCutiHoursPerDay(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="cuti-reason" className="text-xs">
                    Alasan cuti
                  </Label>
                  <Textarea
                    id="cuti-reason"
                    rows={3}
                    value={cutiReason}
                    onChange={(e) => setCutiReason(e.target.value)}
                    placeholder="Mis. Cuti tahunan untuk acara keluarga…"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Bukti</Label>
                  <RadioGroup
                    value={cutiProof}
                    onValueChange={(v) => setCutiProof(v === "terlampir" ? "terlampir" : "tidak terlampir")}
                    className="flex flex-wrap gap-4 pt-1"
                  >
                    <label className="flex items-center gap-2 text-sm">
                      <RadioGroupItem value="terlampir" id="cuti-proof-attached" />
                      Terlampir
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <RadioGroupItem value="tidak terlampir" id="cuti-proof-none" />
                      Tidak terlampir
                    </label>
                  </RadioGroup>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            <div>
              {cutiEditing ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={cutiSaving}
                  onClick={() => setCutiDeleteOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" aria-hidden />
                  Hapus cuti
                </Button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={cutiSaving} onClick={() => setCutiOpen(false)}>
                Batal
              </Button>
              <Button type="button" disabled={cutiSaving} onClick={() => void submitCuti()}>
                {cutiSaving ? "Menyimpan…" : "Simpan"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={cutiDeleteOpen} onOpenChange={(o) => !cutiSaving && setCutiDeleteOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus cuti?</AlertDialogTitle>
            <AlertDialogDescription>
              Data cuti pada kolom <code className="font-mono">rate.paid_leave</code> akan dikosongkan untuk karyawan ini.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cutiSaving}>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cutiSaving}
              onClick={(e) => {
                e.preventDefault();
                void confirmDeleteCuti();
              }}
            >
              {cutiSaving ? "Menghapus…" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

